/**
 * VoxelCraft Game Settings & Laptop Performance Configuration.
 *
 * Provides granular graphics, performance, control, and audio settings
 * designed to optimize VoxelCraft for both battery-constrained laptops
 * and high-end desktop workstations.
 */

import { sound } from "../audio/sound-engine";

export type GraphicsQuality = "fast" | "fancy";

export interface GameSettings {
  /** Render distance in chunk radius (2 to 16). */
  renderDistance: number;
  /** Max FPS cap: 30, 60, 120, or 0 (unlimited). Essential for laptop battery/heat. */
  maxFps: number;
  /** Graphics quality preset: "fast" (1.0x DPI scale) vs "fancy" (native high DPI). */
  graphicsQuality: GraphicsQuality;
  /** Smooth lighting with ambient occlusion. */
  smoothLighting: boolean;
  /** Field of View in degrees (60 to 100, default 75). */
  fov: number;
  /** Mouse & trackpad sensitivity factor (0.25 to 2.0, default 1.0). */
  mouseSensitivity: number;
  /** View bobbing while walking / moving. */
  viewBobbing: boolean;
  /** Dynamic FOV expansion when sprinting or flying. */
  fovEffects: boolean;
  /** Master volume (0.0 to 1.0). */
  masterVolume: number;
  /** Mute audio completely. */
  soundMuted: boolean;
}

export const SETTINGS_KEY = "voxelcraft:settings";

export const MIN_RENDER_DISTANCE = 2;
export const MAX_RENDER_DISTANCE = 16;
export const DEFAULT_RENDER_DISTANCE = 8;

export const FPS_LIMITS = [30, 60, 120, 0] as const;
export type FpsLimit = (typeof FPS_LIMITS)[number];

export function getDefaultSettings(): GameSettings {
  return {
    renderDistance: DEFAULT_RENDER_DISTANCE,
    maxFps: 60,
    graphicsQuality: "fancy",
    smoothLighting: true,
    fov: 75,
    mouseSensitivity: 1.0,
    viewBobbing: true,
    fovEffects: true,
    masterVolume: 0.8,
    soundMuted: false,
  };
}

export function getPresetBatterySettings(): GameSettings {
  return {
    renderDistance: 4,
    maxFps: 30,
    graphicsQuality: "fast",
    smoothLighting: false,
    fov: 75,
    mouseSensitivity: 1.0,
    viewBobbing: true,
    fovEffects: false,
    masterVolume: 0.8,
    soundMuted: false,
  };
}

export function getPresetBalancedSettings(): GameSettings {
  return {
    renderDistance: 6,
    maxFps: 60,
    graphicsQuality: "fast",
    smoothLighting: true,
    fov: 75,
    mouseSensitivity: 1.0,
    viewBobbing: true,
    fovEffects: true,
    masterVolume: 0.8,
    soundMuted: false,
  };
}

export function getPresetMaxSettings(): GameSettings {
  return {
    renderDistance: 12,
    maxFps: 0,
    graphicsQuality: "fancy",
    smoothLighting: true,
    fov: 80,
    mouseSensitivity: 1.0,
    viewBobbing: true,
    fovEffects: true,
    masterVolume: 0.8,
    soundMuted: false,
  };
}

class SettingsManager {
  private current: GameSettings;
  private readonly listeners = new Set<(settings: GameSettings) => void>();

  constructor() {
    this.current = this.load();
    this.applyAudioSettings();
  }

  get settings(): Readonly<GameSettings> {
    return this.current;
  }

  update(patch: Partial<GameSettings>): void {
    this.current = { ...this.current, ...patch };
    this.clamp();
    this.save();
    this.applyAudioSettings();
    this.notify();
  }

  setAll(newSettings: GameSettings): void {
    this.current = { ...newSettings };
    this.clamp();
    this.save();
    this.applyAudioSettings();
    this.notify();
  }

  subscribe(callback: (settings: GameSettings) => void): () => void {
    this.listeners.add(callback);
    callback(this.current);
    return () => this.listeners.delete(callback);
  }

  private notify(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.current);
      } catch (e) {
        console.error("Settings listener error:", e);
      }
    }
  }

  private clamp(): void {
    this.current.renderDistance = Math.max(
      MIN_RENDER_DISTANCE,
      Math.min(MAX_RENDER_DISTANCE, Math.round(this.current.renderDistance)),
    );
    this.current.fov = Math.max(60, Math.min(100, Math.round(this.current.fov)));
    this.current.mouseSensitivity = Math.max(0.25, Math.min(2.0, this.current.mouseSensitivity));
    this.current.masterVolume = Math.max(0.0, Math.min(1.0, this.current.masterVolume));
  }

  private load(): GameSettings {
    const defaults = getDefaultSettings();
    let stored: Record<string, unknown> | null = null;
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch {
      stored = null;
    }

    if (!stored || typeof stored !== "object") {
      return defaults;
    }

    const renderDistance =
      typeof stored.renderDistance === "number" && Number.isFinite(stored.renderDistance)
        ? Math.max(MIN_RENDER_DISTANCE, Math.min(MAX_RENDER_DISTANCE, Math.round(stored.renderDistance)))
        : defaults.renderDistance;

    const maxFps =
      typeof stored.maxFps === "number" && [30, 60, 120, 0].includes(stored.maxFps)
        ? stored.maxFps
        : defaults.maxFps;

    const graphicsQuality: GraphicsQuality =
      stored.graphicsQuality === "fast" || stored.graphicsQuality === "fancy"
        ? stored.graphicsQuality
        : defaults.graphicsQuality;

    const smoothLighting =
      typeof stored.smoothLighting === "boolean" ? stored.smoothLighting : defaults.smoothLighting;

    const fov =
      typeof stored.fov === "number" && Number.isFinite(stored.fov)
        ? Math.max(60, Math.min(100, Math.round(stored.fov)))
        : defaults.fov;

    const mouseSensitivity =
      typeof stored.mouseSensitivity === "number" && Number.isFinite(stored.mouseSensitivity)
        ? Math.max(0.25, Math.min(2.0, stored.mouseSensitivity))
        : defaults.mouseSensitivity;

    const viewBobbing =
      typeof stored.viewBobbing === "boolean" ? stored.viewBobbing : defaults.viewBobbing;

    const fovEffects =
      typeof stored.fovEffects === "boolean" ? stored.fovEffects : defaults.fovEffects;

    let masterVolume = defaults.masterVolume;
    if (typeof stored.masterVolume === "number" && Number.isFinite(stored.masterVolume)) {
      masterVolume = Math.max(0, Math.min(1, stored.masterVolume));
    } else {
      // Fallback check legacy sound_volume
      try {
        const legacyVol = localStorage.getItem("voxelcraft:sound_volume");
        if (legacyVol !== null) masterVolume = Math.max(0, Math.min(1, Number(legacyVol)));
      } catch {}
    }

    let soundMuted = defaults.soundMuted;
    if (typeof stored.soundMuted === "boolean") {
      soundMuted = stored.soundMuted;
    } else {
      // Fallback check legacy sound_muted
      try {
        const legacyMuted = localStorage.getItem("voxelcraft:sound_muted");
        if (legacyMuted !== null) soundMuted = legacyMuted === "true";
      } catch {}
    }

    return {
      renderDistance,
      maxFps,
      graphicsQuality,
      smoothLighting,
      fov,
      mouseSensitivity,
      viewBobbing,
      fovEffects,
      masterVolume,
      soundMuted,
    };
  }

  private save(): void {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.current));
      localStorage.setItem("voxelcraft:sound_volume", String(this.current.masterVolume));
      localStorage.setItem("voxelcraft:sound_muted", String(this.current.soundMuted));
    } catch {
      // Private browsing or quota exceeded: ignore
    }
  }

  private applyAudioSettings(): void {
    sound.volume = this.current.masterVolume;
    sound.muted = this.current.soundMuted;
  }
}

export const settingsManager = new SettingsManager();
